import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    IconButton,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import DateRangePicker from '../../../components/DateRangePicker';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchLedger, setFilters, setPage } from '../../../store/slices/headOfficeFinanceSlice';
import { LedgerEntry } from '../../../services/headOfficeFinanceApi';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

interface HeadOfficeLedgerTabProps {
    ledgerType: string;
}

const formatCurrency = (value: number) =>
    `Rs. ${value.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const HeadOfficeLedgerTab: React.FC<HeadOfficeLedgerTabProps> = ({ ledgerType }) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { currentLedger, filters, loading, error } = useAppSelector(
        (state) => state.headOfficeFinance
    );

    React.useEffect(() => {
        dispatch(
            fetchLedger({
                type: ledgerType,
                from_date: filters.from_date || undefined,
                to_date: filters.to_date || undefined,
                per_page: filters.per_page,
                page: filters.page,
            })
        );
    }, [dispatch, ledgerType, filters.from_date, filters.to_date, filters.per_page, filters.page]);

    const handleDateChange = (from: string, to: string) => {
        dispatch(setFilters({ from_date: from, to_date: to, page: 1 }));
    };

    const handleDateClear = () => {
        dispatch(setFilters({ from_date: '', to_date: '', page: 1 }));
    };

    const handleRefresh = () => {
        dispatch(
            fetchLedger({
                type: ledgerType,
                from_date: filters.from_date || undefined,
                to_date: filters.to_date || undefined,
                per_page: filters.per_page,
                page: filters.page,
            })
        );
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        dispatch(setPage(page));
    };

    const handlePrint = () => {
        const queryParams = new URLSearchParams();
        queryParams.append('type', ledgerType);
        if (filters.from_date) {
            queryParams.append('from_date', filters.from_date);
        }
        if (filters.to_date) {
            queryParams.append('to_date', filters.to_date);
        }

        // Get auth token and add it to the URL
        const token = localStorage.getItem('token');
        if (token) {
            queryParams.append('token', token);
        }

        const printUrl = `${API_BASE_URL}/public/head-office-ledger/print?${queryParams.toString()}`;
        window.open(printUrl, '_blank', 'noopener,noreferrer');
    };

    const renderEntryRow = (entry: LedgerEntry) => (
        <TableRow key={entry.id}>
            <TableCell>{entry.formatted_date}</TableCell>
            <TableCell>{entry.description}</TableCell>
            <TableCell>{entry.head_of_account || '-'}</TableCell>
            <TableCell>{entry.user?.name || 'N/A'}</TableCell>
            <TableCell>{entry.branch?.name || '-'}</TableCell>
            <TableCell align="right">{entry.debit ? formatCurrency(entry.debit) : '-'}</TableCell>
            <TableCell align="right">{entry.credit ? formatCurrency(entry.credit) : '-'}</TableCell>
            <TableCell align="right">{formatCurrency(entry.balance)}</TableCell>
        </TableRow>
    );

    const renderEntryCard = (entry: LedgerEntry) => (
        <Card key={entry.id} variant="outlined">
            <CardContent>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {entry.formatted_date}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {formatCurrency(entry.balance)}
                        </Typography>
                    </Box>
                    <Typography variant="body1">{entry.description}</Typography>
                    {entry.head_of_account && (
                        <Typography variant="caption" color="text.secondary">
                            Head of Account: {entry.head_of_account}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {entry.user?.name || 'N/A'} {entry.branch ? `| ${entry.branch.name}` : ''}
                    </Typography>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Debit
                            </Typography>
                            <Typography variant="body2" color={entry.debit ? 'error.main' : 'text.primary'}>
                                {entry.debit ? formatCurrency(entry.debit) : '-'}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                                Credit
                            </Typography>
                            <Typography variant="body2" color={entry.credit ? 'success.main' : 'text.primary'}>
                                {entry.credit ? formatCurrency(entry.credit) : '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardHeader
                title={currentLedger?.ledger_type.name || 'Ledger'}
                subheader={
                    currentLedger
                        ? `Showing ${currentLedger.pagination.total} entries`
                        : undefined
                }
                action={
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                        <DateRangePicker
                            fromDate={filters.from_date}
                            toDate={filters.to_date}
                            onDateChange={handleDateChange}
                            onClear={handleDateClear}
                            variant="inline"
                        />
                        <Stack direction="row" spacing={1}>
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                onClick={handlePrint}
                                startIcon={<PrintIcon />}
                                disabled={!currentLedger}
                            >
                                Print
                            </Button>
                        </Stack>
                    </Stack>
                }
            />
            <CardContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && !currentLedger && (
                    <Alert severity="info">Loading ledger data...</Alert>
                )}
                {currentLedger && (
                    <Stack spacing={3}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Debit
                                        </Typography>
                                        <Typography variant="h5" color="error">
                                            {formatCurrency(currentLedger.totals.debit)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Credit
                                        </Typography>
                                        <Typography variant="h5" color="success.main">
                                            {formatCurrency(currentLedger.totals.credit)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Date Range
                                        </Typography>
                                        <Typography variant="body1">
                                            {currentLedger.date_range.from || 'All'} -{' '}
                                            {currentLedger.date_range.to || 'All'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {currentLedger.entries.length === 0 ? (
                            <Alert severity="info">
                                No ledger entries found for the selected filters.
                            </Alert>
                        ) : isMobile ? (
                            <Stack spacing={2}>{currentLedger.entries.map(renderEntryCard)}</Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Head of Account</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell>Branch</TableCell>
                                            <TableCell align="right">Debit</TableCell>
                                            <TableCell align="right">Credit</TableCell>
                                            <TableCell align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentLedger.entries.map(renderEntryRow)}
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ fontWeight: 600 }}>
                                                Totals
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(currentLedger.totals.debit)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(currentLedger.totals.credit)}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {currentLedger.pagination.last_page > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                    count={currentLedger.pagination.last_page}
                                    page={currentLedger.pagination.current_page}
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default HeadOfficeLedgerTab;
